// frontend/src/modules/reports/pages/financial/CashTrendPage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Billed-vs-collected trend only (no
// by-client breakdown — that's Revenue's job); reuses the SAME
// BilledCollectedSeries component as RevenuePage per the plan's DRY note.

import * as React from 'react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { reportsApi, downloadReportCsv } from '../../api';
import type { CashTrendReport, RevenueGroupBy } from '../../types';
import { useReportFetch } from './useReportFetch';
import { BilledCollectedSeries } from './BilledCollectedSeries';

const GROUP_BYS: RevenueGroupBy[] = ['week', 'month'];

export function CashTrendPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [groupBy, setGroupBy] = React.useState<RevenueGroupBy>('month');

  const fetcher = range ? () => reportsApi.cashTrend({ from: range.from, to: range.to, groupBy }) : null;
  const result = useReportFetch<CashTrendReport>(fetcher, [range?.from, range?.to, groupBy]);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="Cash trend"
      description="Billed vs. collected cash flow over time."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
          {GROUP_BYS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={[
                'px-3.5 h-8 rounded-full text-[12px] font-semibold capitalize transition-colors',
                groupBy === g ? 'bg-saffron text-zinc-950' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {g}
            </button>
          ))}
        </div>
      }
      csv={range ? {
        filename: `cash-trend-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'cash-trend',
          { from: range.from, to: range.to, groupBy },
          `cash-trend-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && <BilledCollectedSeries rows={rows} />}
    </ReportShell>
  );
}
